import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION, EddYesNo } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types';
import { reportError, ObservabilityErrorSeverity } from '@Services/observability/initObservability';

export const useMilitaryPreciousMetalsLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const formMethods = useFormHook({
    schemaTitle: 'military-precious-metals',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_MILITARY_EQUIPMENT]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_MILITARY_EQUIPMENT) || EddYesNo.NO,
      [EddEmbeddedFields.EDD_PRECIOUS_METALS]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_PRECIOUS_METALS) || EddYesNo.NO,
    },
    formSchema: z.object({
      [EddEmbeddedFields.EDD_MILITARY_EQUIPMENT]: z
        .string()
        .min(1, { message: tValidation('validation.required') }),
      [EddEmbeddedFields.EDD_PRECIOUS_METALS]: z
        .string()
        .min(1, { message: tValidation('validation.required') }),
    }),
  });

  const handleSubmit = async () => {
    const militaryValue = formMethods.getValues(EddEmbeddedFields.EDD_MILITARY_EQUIPMENT);
    const preciousMetalsValue = formMethods.getValues(EddEmbeddedFields.EDD_PRECIOUS_METALS);

    saveEddQuestionToLocalStore({
      question: t('organic.militaryPreciousMetals.militaryTitle'),
      questionId: EddEmbeddedFields.EDD_MILITARY_EQUIPMENT,
      answer: JSON.stringify(militaryValue),
    });
    saveEddQuestionToLocalStore({
      question: t('organic.militaryPreciousMetals.preciousMetalsTitle'),
      questionId: EddEmbeddedFields.EDD_PRECIOUS_METALS,
      answer: JSON.stringify(preciousMetalsValue),
    });

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_MILITARY_PRECIOUS_METALS);
    } catch (error) {
      reportError({
        severity: ObservabilityErrorSeverity.ERROR,
        metadata: {
          name: 'EddMilitaryPreciousMetalsSubmitError',
          message: error instanceof Error ? error.message : String(error),
        },
        groupingHash: 'edd-submit-error',
      });
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
